import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import { SxProps, Theme } from "@mui/material/styles";
import LanguageIcon from "@mui/icons-material/Language";
import "../assets/scss/custom.scss";

interface LanguageSelectorProps {
  isDarkTheme?: boolean;
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  isDarkTheme = false,
}) => {
  const { i18n } = useTranslation();
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const changeLanguage = (lng: string) => {
    // Mudança global do idioma - aplicada em toda a aplicação
    i18n.changeLanguage(lng);
    localStorage.setItem("language", lng);

    // Força atualização global da aplicação
    window.dispatchEvent(new Event("languageChanged"));

    handleClose();
  };

  // Array of languages
  const languages = [
    { code: "en", name: "English", flag: "🇺🇸" },
    { code: "pt", name: "Português", flag: "🇧🇷" },
    { code: "es", name: "Español", flag: "🇪🇸" },
  ];

  // Tamanho padronizado independente do tema
  const BUTTON_SIZE = 44;
  const ICON_SIZE = 22;
  const BORDER_RADIUS = "12px";

  const getButtonStyles = (): SxProps<Theme> => {
    if (isDarkTheme) {
      return {
        background: "rgba(255, 255, 255, 0.1)",
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
        border: "1px solid rgba(255, 255, 255, 0.15)",
        borderRadius: BORDER_RADIUS,
        width: BUTTON_SIZE,
        height: BUTTON_SIZE,
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        position: "relative",
        overflow: "hidden",
        "& .MuiSvgIcon-root": {
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        },
        "&:hover": {
          background: "rgba(138, 79, 255, 1)",
          borderColor: "transparent",
          transform: "translateY(-2px) scale(1.05)",
          boxShadow: "0 8px 32px rgba(138, 79, 255, 0.3)",
        },
        "&:active": {
          transform: "translateY(-1px) scale(1.02)",
        },
      };
    }
    return {
      background: "rgba(255, 255, 255, 0.95)",
      backdropFilter: "blur(20px)",
      WebkitBackdropFilter: "blur(20px)",
      border: "1px solid rgba(121, 86, 247, 0.2)",
      borderRadius: BORDER_RADIUS,
      width: BUTTON_SIZE,
      height: BUTTON_SIZE,
      boxShadow:
        "0 2px 8px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.6)",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      position: "relative",
      overflow: "hidden",
      "& .MuiSvgIcon-root": {
        transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
      },
      "&::before": {
        content: '""',
        position: "absolute",
        inset: 0,
        background: "linear-gradient(135deg, #7956F7 0%, #4C3699 100%)",
        opacity: 0,
        transition: "opacity 0.3s ease",
        borderRadius: "inherit",
      },
      "&:hover": {
        borderColor: "#7956F7",
        boxShadow: "0 6px 20px rgba(121, 86, 247, 0.25)",
        transform: "translateY(-2px) scale(1.05)",
        "&::before": {
          opacity: 1,
        },
      },
      "&:hover .MuiSvgIcon-root": {
        color: "white",
        transform: "rotate(20deg) scale(1.1)",
        filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.2))",
      },
      "&:active": {
        transform: "translateY(-1px) scale(1.02)",
      },
    };
  };

  // Estilo do ícone - padronizado
  const getIconStyles = (): SxProps<Theme> => {
    if (isDarkTheme) {
      return {
        fontSize: ICON_SIZE,
        color: "rgba(255, 255, 255, 0.9)",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        position: "relative",
        zIndex: 1,
      };
    }
    return {
      fontSize: ICON_SIZE,
      color: "#7956F7",
      transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
      position: "relative",
      zIndex: 1,
      filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.1))",
    };
  };

  // Estilos unificados do menu
  const getMenuBackground = () => {
    return "#36276f";
  };

  const getMenuBorder = () => {
    return "1px solid rgba(255, 255, 255, 0.15)";
  };

  // Hide component on screens smaller than 1024px
  if (!isDesktop) {
    return null;
  }

  return (
    <div className="language-selector-container">
      <Tooltip title="Alterar idioma (Global)" arrow>
        <IconButton onClick={handleClick} disableRipple sx={getButtonStyles()}>
          <LanguageIcon sx={getIconStyles()} />
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        sx={{
          zIndex: 10001,
          "& .MuiPaper-root": {
            borderRadius: "16px",
            mt: 1,
            minWidth: 160,
            background: getMenuBackground(),
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
            border: getMenuBorder(),
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
          },
        }}
      >
        {languages.map((language) => (
          <MenuItem
            key={language.code}
            selected={language.code === i18n.language}
            onClick={() => changeLanguage(language.code)}
            sx={{
              borderRadius: "12px",
              fontWeight: 500,
              fontSize: "0.9rem",
              color: "#ffffff",
              "&:hover": {
                backgroundColor: "rgba(138, 79, 255, 0.2)",
              },
              "&.Mui-selected": {
                backgroundColor: "rgba(138, 79, 255, 0.3)",
                color: "#ffffff",
                "&:hover": {
                  backgroundColor: "rgba(138, 79, 255, 0.4)",
                },
              },
            }}
          >
            <span style={{ marginRight: "10px" }}>{language.flag}</span>
            {language.name}
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
};

export default LanguageSelector;
